import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import RadioButton from 'components/buttons/RadioButton';
import RedButton from 'components/buttons/RedButton';
import { RedTextButton } from 'components/buttons/RedTextButton';
import { H4, H6 } from 'components/typography/Headers';
import { Text1, Text2 } from 'components/typography/Texts';
import { OrganizationFormData } from 'features/my-profile/become-fundraiser/form/components/add-organization/useAddOrganizationForm';
import { ProposedOrganizationDto } from 'services/user/userService.dto';

import { proposedToOrganizationFormData } from './organizationValidation';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 500px;
  gap: 24px;
  text-align: center;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const OptionCard = styled.div<{ $active: boolean }>`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border: 1px solid ${props => (props.$active ? theme.color.red : theme.color.borderGray)};
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
`;

const OptionContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

const Highlighted = styled.span<{ $highlighted?: boolean }>`
  color: ${props => (props.$highlighted ? theme.color.green : theme.color.darkerGray)};
  font-weight: ${props => (props.$highlighted ? 'bold' : 'normal')};
`;

const Details = styled(Text2)`
  color: ${theme.color.lighterGray2};
  white-space: pre-wrap;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Button = styled(RedButton)`
  height: 46px;
`;

const ChangeDataButton = styled(RedTextButton)`
  width: 100%;
`;

interface Props {
  enteredOrganization: OrganizationFormData;
  proposedOrganization: ProposedOrganizationDto;
  onSelected: (organization: OrganizationFormData) => void;
  onEditEntered: () => void;
}

const ProposedOrganization = ({ enteredOrganization, proposedOrganization, onSelected, onEditEntered }: Props) => {
  const { t } = useTranslation('myProfile');
  const [useProposed, setUseProposed] = useState(true);

  const enteredStreet = `${enteredOrganization.addressLine1} ${enteredOrganization.addressLine2 || ''}`.trim();
  const fieldsToMark = {
    name: proposedOrganization.organizationName !== enteredOrganization.name.trim().toUpperCase(),
    street: proposedOrganization.street !== enteredStreet.toUpperCase(),
    city: proposedOrganization.city !== enteredOrganization.city.trim().toUpperCase(),
    state: proposedOrganization.state !== enteredOrganization.state,
    zipCode: proposedOrganization.zipCode !== enteredOrganization.zipCode,
  };

  const handleNext = () => {
    onSelected(useProposed ? proposedToOrganizationFormData(proposedOrganization) : enteredOrganization);
  };

  return (
    <Container data-testid={'organization-proposed'}>
      <H4>{t('become-fundraiser.verify-organization.proposed-organization-header')}</H4>
      <Text1>{t('become-fundraiser.verify-organization.proposed-organization-description')}</Text1>
      <Options>
        <OptionCard $active={!useProposed} onClick={() => setUseProposed(false)} data-testid={'entered-organization'}>
          <RadioButton checked={!useProposed} onChange={() => setUseProposed(false)} />
          <OptionContent>
            <H6>{enteredOrganization.name}</H6>
            <Details>
              {enteredStreet}
              {`\n${enteredOrganization.city}, ${enteredOrganization.state} ${enteredOrganization.zipCode}`}
              {`\n${t('form-inputs.ein-placeholder')}: ${enteredOrganization.ein}`}
            </Details>
          </OptionContent>
        </OptionCard>

        <OptionCard $active={useProposed} onClick={() => setUseProposed(true)} data-testid={'proposed-organization'}>
          <RadioButton checked={useProposed} onChange={() => setUseProposed(true)} />
          <OptionContent>
            <H6>
              <Highlighted $highlighted={fieldsToMark.name}>{proposedOrganization.organizationName}</Highlighted>
            </H6>
            <Details>
              <Highlighted $highlighted={fieldsToMark.street}>{proposedOrganization.street}</Highlighted>
              {`\n`}
              <Highlighted $highlighted={fieldsToMark.city}>{proposedOrganization.city}</Highlighted>
              {`, `}
              <Highlighted $highlighted={fieldsToMark.state}>{proposedOrganization.state}</Highlighted>
              {` `}
              <Highlighted $highlighted={fieldsToMark.zipCode}>{proposedOrganization.zipCode}</Highlighted>
              {`\n${t('form-inputs.ein-placeholder')}: ${proposedOrganization.ein}`}
            </Details>
          </OptionContent>
        </OptionCard>
      </Options>
      <ButtonsContainer>
        <Button onClick={handleNext}>{t('common:next')}</Button>
        <ChangeDataButton data-testid={'organization-proposed-change-data'} onClick={onEditEntered}>
          {t('become-fundraiser.verify-organization.change-organization-data')}
        </ChangeDataButton>
      </ButtonsContainer>
    </Container>
  );
};

export default ProposedOrganization;
